// Portal v2 Slice F — staff Messages inbox (2026-08-30).
//
// The rep-side consumer of the portal's client↔rep threads. List on the
// left (unread-first feel via badge + recency order), detail + reply on
// the right. Replying emails the client's portal users a deep link.
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { api, ApiError } from "@/lib/api";
import { timeAgo } from "@/lib/utils";

interface InboxThread {
  id: string;
  clientId: string;
  clientName: string;
  subject: string | null;
  projectName: string | null;
  lastMessageAt: string;
  unreadCount: number;
}

interface ThreadDetail {
  id: string;
  clientId: string;
  clientName: string;
  subject: string | null;
  projectName: string | null;
  messages: Array<{
    id: string;
    authorSide: "client" | "staff";
    authorName: string | null;
    body: string;
    createdAt: string;
  }>;
}

export function MessagesInboxPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    data: listData,
    isError: listError,
    error: listErrorObj,
  } = useQuery({
    queryKey: ["message-threads"],
    queryFn: () => api<{ threads: InboxThread[] }>("/message-threads"),
  });
  const { data: thread, isError: threadError } = useQuery({
    queryKey: ["message-thread", id],
    queryFn: () => api<ThreadDetail>(`/message-threads/${id}`),
    enabled: typeof id === "string" && id.length > 0,
  });

  const [reply, setReply] = useState("");
  // A half-typed reply must NEVER follow the rep to a different client's
  // thread (UX-review blocker 2026-08-30 — that's a confidentiality leak,
  // not a glitch). Thread switch clears the draft.
  useEffect(() => setReply(""), [id]);
  // Opening a thread stamps the read cursor server-side; the list's badge
  // must stop claiming unread on the same screen. Invalidate on load.
  useEffect(() => {
    if (thread) {
      void queryClient.invalidateQueries({ queryKey: ["message-threads"] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thread?.id]);

  const forbidden =
    listError &&
    listErrorObj instanceof ApiError &&
    (listErrorObj.status === 403 || listErrorObj.status === 401);
  const replyMutation = useMutation({
    mutationFn: () =>
      api(`/message-threads/${id}/reply`, {
        method: "POST",
        body: JSON.stringify({ body: reply.trim() }),
      }),
    onSuccess: () => {
      setReply("");
      void queryClient.invalidateQueries({ queryKey: ["message-thread", id] });
      void queryClient.invalidateQueries({ queryKey: ["message-threads"] });
    },
  });

  const threads = listData?.threads ?? [];

  return (
    <div className="flex h-full flex-col gap-6 md:flex-row">
      {/* Thread list */}
      <aside className={(id ? "hidden md:block " : "") + "w-full shrink-0 overflow-y-auto md:w-80"}>
        <h1 className="mb-4 flex items-center gap-2 font-display text-xl font-semibold tracking-tight text-brown-500">
          <MessageSquare className="h-5 w-5" /> Messages
        </h1>
        {forbidden ? (
          <div className="rounded-lg border border-dashed border-brown-100 bg-white px-6 py-12 text-center text-sm text-brown-300">
            Messages is available to sales reps and admins. Ask an admin if
            you need access.
          </div>
        ) : listError ? (
          <div className="rounded-lg border border-dashed border-brown-100 bg-white px-6 py-12 text-center text-sm text-brown-300">
            Something went wrong loading messages — refresh to try again.
          </div>
        ) : threads.length === 0 ? (
          <div className="rounded-lg border border-dashed border-brown-100 bg-white px-6 py-12 text-center text-sm text-brown-300">
            No client messages yet. Threads clients start in their portal
            land here.
          </div>
        ) : (
          <ul className="space-y-1">
            {threads.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  onClick={() => navigate(`/messages/${t.id}`)}
                  className={
                    "w-full rounded-md px-3 py-2 text-left transition-colors " +
                    (t.id === id
                      ? "bg-peach-100"
                      : "hover:bg-cream-100")
                  }
                >
                  <span className="flex items-center justify-between gap-2">
                    <span className="truncate text-sm font-semibold text-brown-500">
                      {t.clientName}
                    </span>
                    {t.unreadCount > 0 && (
                      <span className="rounded-full bg-ink px-2 py-0.5 text-xs font-bold text-white tabular-nums">
                        {t.unreadCount}
                      </span>
                    )}
                  </span>
                  <span className="block truncate text-xs text-brown-400">
                    {t.subject ?? t.projectName ?? "General"} ·{" "}
                    {timeAgo(t.lastMessageAt)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </aside>

      {/* Detail */}
      <section className="min-w-0 flex-1 overflow-y-auto">
        {!id ? (
          <p className="mt-12 hidden text-center text-sm text-brown-300 md:block">
            Pick a conversation.
          </p>
        ) : threadError ? (
          <p className="mt-12 text-center text-sm text-brown-300">
            Something went wrong loading this conversation — refresh to try
            again.
          </p>
        ) : !thread ? (
          <p className="mt-12 text-center text-sm text-brown-300">Loading…</p>
        ) : (
          <>
            <button
              type="button"
              onClick={() => navigate("/messages")}
              className="mb-3 text-sm font-semibold text-ink underline-offset-2 hover:underline md:hidden"
            >
              ← All messages
            </button>
            <div className="mb-4">
              <h2 className="text-lg font-bold text-brown-500">
                {thread.clientName}
                <span className="font-normal text-brown-400">
                  {" — "}
                  {thread.subject ?? thread.projectName ?? "General"}
                </span>
              </h2>
            </div>
            <div className="mb-4 space-y-3">
              {thread.messages.map((m) => (
                <div
                  key={m.id}
                  className={
                    "max-w-[85%] rounded-lg border px-4 py-3 " +
                    (m.authorSide === "staff"
                      ? "ml-auto border-peach-300 bg-peach-50"
                      : "border-brown-100 bg-white")
                  }
                >
                  <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-brown-300">
                    {m.authorName ?? (m.authorSide === "client" ? "Client" : "Staff")}
                    {" · "}
                    {timeAgo(m.createdAt)}
                  </div>
                  <p className="whitespace-pre-wrap text-sm text-brown-500">
                    {m.body}
                  </p>
                </div>
              ))}
            </div>
            <div className="rounded-lg border border-brown-100 bg-white p-4">
              <textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                maxLength={4000}
                rows={3}
                placeholder="Reply to the client — they're emailed a link to read it in their portal…"
                className="mb-3 w-full rounded-md border border-brown-100 bg-white p-2.5 text-sm text-brown-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-peach-400 focus-visible:ring-offset-2 focus-visible:ring-offset-cream-50"
              />
              <Button
                disabled={reply.trim().length === 0 || replyMutation.isPending}
                onClick={() => replyMutation.mutate()}
              >
                {replyMutation.isPending ? "Sending…" : "Send reply"}
              </Button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
